import { useAuth, useUser } from "@clerk/react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { getApiBaseUrl } from "../api/baseUrl"
import { isRateLimitError, throwIfRateLimited } from "../api/rateLimit"
import { useRemoteAccessDecision } from "../auth/useRemoteAccess"
import {
  CARD_LIBRARY_STORAGE_KEY,
  CARD_LIBRARY_STORAGE_VERSION,
  CardLibraryStateSchema,
  EMPTY_CARD_LIBRARY,
} from "../cards/storage"
import {
  CHALLENGE_STORE_KEY,
  CHALLENGE_STORE_VERSION,
  type ChallengeRecord,
  ChallengeStoreSchema,
  challengeListToStore,
  challengeStoreToList,
  createChallengeId,
  EMPTY_CHALLENGE_STORE,
} from "../challenges/storage"
import {
  DECK_LIBRARY_STORAGE_KEY,
  DECK_LIBRARY_STORAGE_VERSION,
  DeckLibraryStateSchema,
  EMPTY_DECK_LIBRARY,
} from "../decks/storage"
import {
  EMPTY_HERO_LIBRARY,
  HERO_LIBRARY_STORAGE_KEY,
  HERO_LIBRARY_STORAGE_VERSION,
  HeroLibraryStateSchema,
} from "../heroes/storage"
import { useUserIdbState } from "../hooks/useIdbState"
import { useStoredState } from "../hooks/useStoredState"
import { useI18n } from "../i18n/useI18n"
import {
  LibraryRefreshStatus,
  RecordOriginBadge,
} from "../library/LibrarySyncIndicators"
import { mergeAuthoritativeRemoteRecords } from "../library/merge"
import { fetchManagedDeckRecordsFromServer } from "../library/remote"
import {
  markRecordSyncFailed,
  markRecordSyncPending,
  markRecordSyncSucceeded,
  shouldAutoRetrySync,
} from "../library/syncState"
import { createMatch } from "../match/engine/match/init"
import { getMatchSetupErrorLabel } from "../match/errorMessages"
import { buildSeatInput } from "../match/matchUtils"
import {
  storePendingMatch,
  storePendingOnlineMatch,
} from "../match/pendingMatch"
import {
  createPlayerReport,
  EMPTY_PLAYER_REPORT_STORE,
  PLAYER_REPORT_STORAGE_KEY,
  PLAYER_REPORT_STORAGE_VERSION,
  PlayerReportStoreSchema,
} from "../moderation/reports"
import { ProfileBootstrap } from "../profile/ProfileBootstrap"
import {
  buildPublicPlayerProfile,
  createFriendCode,
} from "../profile/publicProfile"
import {
  EMPTY_PLAYER_PROFILE_STORE,
  EMPTY_PUBLIC_PLAYER_PROFILES,
  PLAYER_PROFILES_STORAGE_KEY,
  PLAYER_PROFILES_STORAGE_VERSION,
  PlayerProfileStoreStateSchema,
  PUBLIC_PLAYER_PROFILES_STORAGE_KEY,
  PUBLIC_PLAYER_PROFILES_STORAGE_VERSION,
  PublicPlayerProfilesStateSchema,
} from "../profile/storage"
import {
  getProfileByEmail,
  normalizeEmail,
  validateDisplayName,
} from "../profile/validation"
import { useSessionQuery } from "../query/authenticated"
import { queryKeys } from "../query/keys"
import { useAppRouter } from "../router/useAppRouter"
import type { PlayerProfile } from "../schemas"
import {
  useLookupPublicProfileByFriendCode,
  usePublishPublicProfileMutation,
  useSocialSnapshotQuery,
  useSyncChallengeMutation,
  useSyncFriendRequestMutation,
} from "../social/query"
import { usePublicSystemBattleBotsQuery } from "../system/query"
import {
  EMPTY_SYSTEM_BATTLE_BOTS,
  SYSTEM_BATTLE_BOTS_STORAGE_KEY,
  SYSTEM_BATTLE_BOTS_STORAGE_VERSION,
  SystemBattleBotsStateSchema,
} from "../system/storage"
import {
  createFriendRequestId,
  EMPTY_FRIEND_ALIAS_STORE,
  EMPTY_FRIEND_REQUEST_STORE,
  FRIEND_ALIAS_STORE_KEY,
  FRIEND_ALIAS_STORE_VERSION,
  FRIEND_REQUEST_STORE_KEY,
  FRIEND_REQUEST_STORE_VERSION,
  FriendAliasStoreSchema,
  type FriendRequestRecord,
  FriendRequestStoreSchema,
  friendRequestListToStore,
  friendRequestStoreToList,
} from "./storage"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Tab = "players" | "challenges"
type SocialProfile = {
  alias?: string
  avatarUrl?: string
  gamerTag: string
  ownerUserId?: string
  updatedAt?: string
}

// ---------------------------------------------------------------------------
// Challenge deck picker (inline)
// ---------------------------------------------------------------------------

function DeckPicker({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (id: string) => void
}) {
  const { t } = useI18n()
  const [decks] = useUserIdbState(
    DECK_LIBRARY_STORAGE_KEY,
    DeckLibraryStateSchema,
    {
      defaultValue: EMPTY_DECK_LIBRARY,
      version: DECK_LIBRARY_STORAGE_VERSION,
    },
  )
  return (
    <label className="field">
      <span>{label}</span>
      <select
        className="text-input"
        onChange={(e) => onChange(e.target.value)}
        value={value}
      >
        <option value="">{t("friends.deckPicker.placeholder")}</option>
        {decks.map((r) => (
          <option key={r.deck.deckId} value={r.deck.deckId}>
            {r.deck.name}
          </option>
        ))}
      </select>
    </label>
  )
}

// ---------------------------------------------------------------------------
// Players list
// ---------------------------------------------------------------------------

function PlayersList({
  myTag,
  profiles,
  challenges,
  reports,
  onChallenge,
  onSetAlias,
  onReport,
}: {
  myTag: string
  profiles: SocialProfile[]
  challenges: ChallengeRecord[]
  reports: {
    reporterTag: string
    reportedTag: string
    status: "pending" | "reviewed" | "dismissed"
  }[]
  onChallenge: (challengedTag: string, deckId: string) => void
  onSetAlias: (ownerUserId: string, alias: string | null) => void
  onReport: (reportedTag: string, reason: string) => void
}) {
  const { t } = useI18n()
  const others = profiles.filter((p) => p.gamerTag !== myTag)
  const [challengeTag, setChallengeTag] = useState<string | null>(null)
  const [aliasTag, setAliasTag] = useState<string | null>(null)
  const [aliasValue, setAliasValue] = useState("")
  const [aliasError, setAliasError] = useState<string | null>(null)
  const [reportTag, setReportTag] = useState<string | null>(null)
  const [deckId, setDeckId] = useState("")
  const [reportReason, setReportReason] = useState("")
  const [formError, setFormError] = useState<string | null>(null)
  const [reportError, setReportError] = useState<string | null>(null)

  if (others.length === 0) {
    return <p className="meta">{t("friends.noPlayers")}</p>
  }

  const pendingTo = (tag: string) =>
    challenges.some(
      (c) =>
        c.challengerTag === myTag &&
        c.challengedTag === tag &&
        c.status === "pending",
    )

  const pendingReportTo = (tag: string) =>
    reports.some(
      (report) =>
        report.reporterTag === myTag &&
        report.reportedTag === tag &&
        report.status === "pending",
    )

  const handleSend = (challengedTag: string) => {
    if (!deckId) {
      setFormError(t("friends.selectDeckToChallenge"))
      return
    }
    onChallenge(challengedTag, deckId)
    setChallengeTag(null)
    setDeckId("")
    setFormError(null)
  }

  const handleReport = (reportedTag: string) => {
    const trimmedReason = reportReason.trim()
    if (trimmedReason.length < 8) {
      setReportError(t("friends.reportError.reasonTooShort"))
      return
    }
    onReport(reportedTag, trimmedReason)
    setReportTag(null)
    setReportReason("")
    setReportError(null)
  }

  const handleSaveAlias = (profile: SocialProfile) => {
    if (!profile.ownerUserId) return
    const trimmedAlias = aliasValue.trim()
    if (trimmedAlias.length > 0) {
      const validationError = validateDisplayName(trimmedAlias)
      if (validationError === "length") {
        setAliasError(t("friends.alias.error.length"))
        return
      }
      if (validationError === "blocked") {
        setAliasError(t("friends.alias.error.blocked"))
        return
      }
    }

    onSetAlias(
      profile.ownerUserId,
      trimmedAlias.length > 0 ? trimmedAlias : null,
    )
    setAliasError(null)
    setAliasTag(null)
    setAliasValue("")
  }

  return (
    <div className="players-list">
      {others.map((p) => {
        const hasPending = pendingTo(p.gamerTag)
        const hasPendingReport = pendingReportTo(p.gamerTag)
        const isAliasExpanded = aliasTag === p.gamerTag
        const isChallengeExpanded = challengeTag === p.gamerTag
        const isReportExpanded = reportTag === p.gamerTag
        const canChallengeOnline = Boolean(p.ownerUserId)
        const lastSeen = p.updatedAt ? new Date(p.updatedAt).getTime() : null
        const recentlySeen =
          typeof lastSeen === "number" &&
          Number.isFinite(lastSeen) &&
          Date.now() - lastSeen <= 5 * 60_000

        return (
          <div className="player-row" key={p.gamerTag}>
            <div className="player-row-identity">
              <div className="player-row-profile">
                {p.avatarUrl ? (
                  <img
                    alt={p.gamerTag}
                    className="player-row-avatar"
                    src={p.avatarUrl}
                  />
                ) : (
                  <span className="player-row-avatar player-row-avatar-fallback">
                    {p.gamerTag.slice(0, 1).toUpperCase()}
                  </span>
                )}
                <div className="player-row-profile-copy">
                  <strong>{p.gamerTag}</strong>
                  {p.alias && p.alias !== p.gamerTag && (
                    <span className="meta">{p.alias}</span>
                  )}
                  <div className="player-row-statuses">
                    {canChallengeOnline && recentlySeen && (
                      <span className="status-badge status-badge-ok">
                        {t("friends.playerRecentlySeen")}
                      </span>
                    )}
                    {canChallengeOnline && !recentlySeen && p.updatedAt && (
                      <span className="status-badge status-badge-muted">
                        {t("friends.playerSeenEarlier")}
                      </span>
                    )}
                    {!canChallengeOnline && (
                      <span className="status-badge status-badge-muted">
                        {t("friends.playerUnavailable")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="player-row-actions">
              {hasPending ? (
                <span className="status-badge status-badge-muted">
                  {t("friends.challengeSent")}
                </span>
              ) : (
                <button
                  className="secondary-button"
                  disabled={!canChallengeOnline}
                  onClick={() => {
                    setChallengeTag(isChallengeExpanded ? null : p.gamerTag)
                    setAliasTag(null)
                    setReportTag(null)
                    setDeckId("")
                    setFormError(null)
                  }}
                  type="button"
                >
                  {!canChallengeOnline
                    ? t("friends.playerUnavailable")
                    : isChallengeExpanded
                      ? t("friends.challengeCancel")
                      : t("friends.challengeLabel")}
                </button>
              )}
              {p.ownerUserId ? (
                <button
                  className="ghost-icon-button"
                  onClick={() => {
                    setAliasTag(isAliasExpanded ? null : p.gamerTag)
                    setAliasValue(p.alias ?? "")
                    setAliasError(null)
                    setChallengeTag(null)
                    setReportTag(null)
                  }}
                  type="button"
                >
                  {isAliasExpanded
                    ? t("common.cancel")
                    : p.alias
                      ? t("friends.alias.edit")
                      : t("friends.alias.add")}
                </button>
              ) : null}
              {hasPendingReport ? (
                <span className="status-badge status-badge-muted">
                  {t("friends.reportSent")}
                </span>
              ) : (
                <button
                  className="ghost-icon-button danger-text"
                  onClick={() => {
                    setReportTag(isReportExpanded ? null : p.gamerTag)
                    setChallengeTag(null)
                    setReportReason("")
                    setReportError(null)
                  }}
                  type="button"
                >
                  {isReportExpanded
                    ? t("friends.challengeCancelReport")
                    : t("common.report")}
                </button>
              )}
            </div>
            {isChallengeExpanded && (
              <div className="player-challenge-form">
                <DeckPicker
                  label={t("friends.deckPicker.label")}
                  onChange={setDeckId}
                  value={deckId}
                />
                {formError && (
                  <strong className="field-error">{formError}</strong>
                )}
                <button
                  className="primary-button"
                  onClick={() => handleSend(p.gamerTag)}
                  type="button"
                >
                  {t("friends.sendChallenge")}
                </button>
              </div>
            )}
            {isAliasExpanded && p.ownerUserId && (
              <div className="player-challenge-form">
                <label className="field">
                  <span>{t("friends.alias.label")}</span>
                  <input
                    className="text-input"
                    maxLength={30}
                    onChange={(event) => setAliasValue(event.target.value)}
                    placeholder={t("friends.alias.placeholder")}
                    type="text"
                    value={aliasValue}
                  />
                </label>
                {aliasError && (
                  <strong className="field-error">{aliasError}</strong>
                )}
                <div className="button-row">
                  <button
                    className="primary-button"
                    onClick={() => handleSaveAlias(p)}
                    type="button"
                  >
                    {t("common.saveChanges")}
                  </button>
                  {p.alias ? (
                    <button
                      className="ghost-icon-button danger-text"
                      onClick={() => {
                        if (!p.ownerUserId) return
                        onSetAlias(p.ownerUserId, null)
                        setAliasTag(null)
                        setAliasValue("")
                        setAliasError(null)
                      }}
                      type="button"
                    >
                      {t("friends.alias.clear")}
                    </button>
                  ) : null}
                </div>
              </div>
            )}
            {isReportExpanded && (
              <div className="player-report-form">
                <label className="field">
                  <span>{t("friends.reportReasonLabel")}</span>
                  <textarea
                    className="text-input text-area"
                    onChange={(event) => setReportReason(event.target.value)}
                    placeholder={t("friends.reportPlaceholder")}
                    rows={3}
                    value={reportReason}
                  />
                </label>
                {reportError && (
                  <strong className="field-error">{reportError}</strong>
                )}
                <button
                  className="primary-button"
                  onClick={() => handleReport(p.gamerTag)}
                  type="button"
                >
                  {t("friends.submitReport")}
                </button>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Challenge inbox
// ---------------------------------------------------------------------------

function ChallengesPanel({
  myTag,
  challenges,
  profiles,
  errorMessage,
  onAccept,
  onDecline,
  onCancel,
  onJoin,
}: {
  myTag: string
  challenges: ChallengeRecord[]
  profiles: SocialProfile[]
  errorMessage: string | null
  onAccept: (challengeId: string, deckId: string) => void
  onDecline: (challengeId: string) => void
  onCancel: (challengeId: string) => void
  onJoin: (matchId: string, seatId: "seat_a" | "seat_b") => void
}) {
  const { t } = useI18n()
  const [acceptingId, setAcceptingId] = useState<string | null>(null)
  const [acceptDeck, setAcceptDeck] = useState("")
  const [acceptError, setAcceptError] = useState<string | null>(null)

  const received = challenges
    .filter((c) => c.challengedTag === myTag && c.status === "pending")
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

  const sent = challenges
    .filter((c) => c.challengerTag === myTag)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

  const deckNameFor = (deckId: string | undefined) => deckId ?? "—"

  const profileFor = (tag: string) => profiles.find((p) => p.gamerTag === tag)

  const handleConfirmAccept = (challengeId: string) => {
    if (!acceptDeck) {
      setAcceptError(t("friends.acceptError.selectDeck"))
      return
    }
    onAccept(challengeId, acceptDeck)
    setAcceptingId(null)
    setAcceptDeck("")
    setAcceptError(null)
  }

  return (
    <div className="challenges-panel">
      {errorMessage && <strong className="field-error">{errorMessage}</strong>}
      <section>
        <h3 className="challenges-section-heading">
          {t("friends.challenges.received")}
        </h3>
        {received.length === 0 ? (
          <p className="meta">{t("friends.challenges.emptyReceived")}</p>
        ) : (
          received.map((c) => {
            const challenger = profileFor(c.challengerTag)
            const isAccepting = acceptingId === c.challengeId
            return (
              <div className="challenge-row" key={c.challengeId}>
                <div className="challenge-row-info">
                  <strong>{c.challengerTag}</strong>
                  {challenger?.alias && challenger.alias !== c.challengerTag ? (
                    <span className="meta">{challenger.alias}</span>
                  ) : null}
                  <RecordOriginBadge record={c} />
                  <span className="meta">
                    {t("common.deck")}: {deckNameFor(c.challengerDeckId)}
                  </span>
                </div>
                <div className="challenge-row-actions">
                  {isAccepting ? (
                    <div className="challenge-accept-form">
                      <DeckPicker
                        label={t("friends.deckPicker.label")}
                        onChange={setAcceptDeck}
                        value={acceptDeck}
                      />
                      {acceptError && (
                        <strong className="field-error">{acceptError}</strong>
                      )}
                      <div className="button-row">
                        <button
                          className="primary-button"
                          onClick={() => handleConfirmAccept(c.challengeId)}
                          type="button"
                        >
                          {t("friends.challenges.startMatch")}
                        </button>
                        <button
                          className="ghost-icon-button"
                          onClick={() => {
                            setAcceptingId(null)
                            setAcceptDeck("")
                            setAcceptError(null)
                          }}
                          type="button"
                        >
                          {t("common.cancel")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button
                        className="primary-button"
                        onClick={() => setAcceptingId(c.challengeId)}
                        type="button"
                      >
                        {t("common.accept")}
                      </button>
                      <button
                        className="ghost-icon-button danger-text"
                        onClick={() => onDecline(c.challengeId)}
                        type="button"
                      >
                        {t("common.decline")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )
          })
        )}
      </section>

      <section>
        <h3 className="challenges-section-heading">
          {t("friends.challenges.sent")}
        </h3>
        {sent.length === 0 ? (
          <p className="meta">{t("friends.challenges.emptySent")}</p>
        ) : (
          sent.map((c) => {
            const matchId = c.matchId
            return (
              <div className="challenge-row" key={c.challengeId}>
                <div className="challenge-row-info">
                  <strong>{c.challengedTag}</strong>
                  <RecordOriginBadge record={c} />
                  <span
                    className={
                      c.status === "pending"
                        ? "status-badge status-badge-muted"
                        : c.status === "accepted"
                          ? "status-badge status-badge-ok"
                          : "status-badge status-badge-muted"
                    }
                  >
                    {t(`common.status.${c.status}` as const)}
                  </span>
                </div>
                <div className="challenge-row-actions">
                  {c.status === "pending" && (
                    <button
                      className="ghost-icon-button danger-text"
                      onClick={() => onCancel(c.challengeId)}
                      type="button"
                    >
                      {t("common.cancel")}
                    </button>
                  )}
                  {c.status === "accepted" && matchId && (
                    <button
                      className="primary-button"
                      onClick={() => onJoin(matchId, "seat_a")}
                      type="button"
                    >
                      {t("friends.challenges.joinMatch")}
                    </button>
                  )}
                </div>
              </div>
            )
          })
        )}
      </section>
    </div>
  )
}

function mergeRemoteChallengeStore(
  localStore: Record<string, ChallengeRecord>,
  remoteRecords: readonly ChallengeRecord[],
): Record<string, ChallengeRecord> {
  const merged = mergeAuthoritativeRemoteRecords(
    challengeStoreToList(localStore),
    remoteRecords,
    (record) => record.challengeId,
  )
  return challengeListToStore(merged)
}

function mergeRemoteFriendRequestStore(
  localStore: Record<string, FriendRequestRecord>,
  remoteRecords: readonly FriendRequestRecord[],
): Record<string, FriendRequestRecord> {
  const merged = mergeAuthoritativeRemoteRecords(
    friendRequestStoreToList(localStore),
    remoteRecords,
    (record) => record.requestId,
  )
  return friendRequestListToStore(merged)
}

function FriendCodePanel({
  friendCode,
  onRegenerate,
  avatarUrl,
  accountName,
  gamerTag,
}: {
  friendCode: string
  onRegenerate: () => void
  avatarUrl?: string | null
  accountName?: string | null
  gamerTag: string
}) {
  const { t } = useI18n()
  const [copied, setCopied] = useState(false)

  async function handleCopyCode() {
    try {
      await navigator.clipboard.writeText(friendCode)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1_500)
    } catch {
      setCopied(false)
    }
  }

  return (
    <section className="friends-code-panel">
      <div className="friends-code-panel-header">
        <div className="friends-self-summary">
          {avatarUrl ? (
            <img
              alt={accountName || gamerTag}
              className="friends-self-avatar"
              src={avatarUrl}
            />
          ) : (
            <span className="friends-self-avatar friends-self-avatar-fallback">
              {(accountName || gamerTag).slice(0, 1).toUpperCase()}
            </span>
          )}
          <div className="friends-self-copy">
            <strong>{accountName || gamerTag}</strong>
            <span>{gamerTag}</span>
          </div>
        </div>
        <div>
          <strong>{t("friends.friendCode.label")}</strong>
          <p className="meta">{t("friends.friendCode.help")}</p>
        </div>
      </div>
      <div className="friends-code-row">
        <code className="friends-code-chip">{friendCode}</code>
        <button
          className="secondary-button"
          onClick={handleCopyCode}
          type="button"
        >
          {copied ? t("common.copied") : t("common.copy")}
        </button>
        <button
          className="ghost-icon-button"
          onClick={onRegenerate}
          type="button"
        >
          {t("friends.friendCode.regenerate")}
        </button>
      </div>
    </section>
  )
}

function FriendRequestSections({
  incoming,
  outgoing,
  onAccept,
  onCancel,
  onDecline,
}: {
  incoming: FriendRequestRecord[]
  outgoing: FriendRequestRecord[]
  onAccept: (requestId: string) => void
  onCancel: (requestId: string) => void
  onDecline: (requestId: string) => void
}) {
  const { t } = useI18n()

  return (
    <div className="friends-request-sections">
      <section>
        <h3 className="challenges-section-heading">
          {t("friends.requests.incoming")}
        </h3>
        {incoming.length === 0 ? (
          <p className="meta">{t("friends.requests.emptyIncoming")}</p>
        ) : (
          incoming.map((request) => (
            <div className="challenge-row" key={request.requestId}>
              <div className="challenge-row-info">
                <strong>{request.requesterTag}</strong>
                <RecordOriginBadge record={request} />
              </div>
              <div className="challenge-row-actions">
                <button
                  className="primary-button"
                  onClick={() => onAccept(request.requestId)}
                  type="button"
                >
                  {t("common.accept")}
                </button>
                <button
                  className="ghost-icon-button danger-text"
                  onClick={() => onDecline(request.requestId)}
                  type="button"
                >
                  {t("common.decline")}
                </button>
              </div>
            </div>
          ))
        )}
      </section>

      <section>
        <h3 className="challenges-section-heading">
          {t("friends.requests.outgoing")}
        </h3>
        {outgoing.length === 0 ? (
          <p className="meta">{t("friends.requests.emptyOutgoing")}</p>
        ) : (
          outgoing.map((request) => (
            <div className="challenge-row" key={request.requestId}>
              <div className="challenge-row-info">
                <strong>{request.inviterTag}</strong>
                <RecordOriginBadge record={request} />
                <span className="status-badge status-badge-muted">
                  {t("common.status.pending")}
                </span>
              </div>
              <div className="challenge-row-actions">
                <button
                  className="ghost-icon-button danger-text"
                  onClick={() => onCancel(request.requestId)}
                  type="button"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function FriendsContent({ myProfile }: { myProfile: PlayerProfile }) {
  const { t } = useI18n()
  const { getToken, userId } = useAuth()
  const { user } = useUser()
  const myTag = myProfile.gamerTag
  const [tab, setTab] = useState<Tab>("players")
  const [actionError, setActionError] = useState<string | null>(null)
  const router = useAppRouter()

  const [, setProfiles] = useStoredState(
    PLAYER_PROFILES_STORAGE_KEY,
    PlayerProfileStoreStateSchema,
    {
      defaultValue: EMPTY_PLAYER_PROFILE_STORE,
      version: PLAYER_PROFILES_STORAGE_VERSION,
    },
  )
  const [publicProfiles, setPublicProfiles] = useStoredState(
    PUBLIC_PLAYER_PROFILES_STORAGE_KEY,
    PublicPlayerProfilesStateSchema,
    {
      defaultValue: EMPTY_PUBLIC_PLAYER_PROFILES,
      version: PUBLIC_PLAYER_PROFILES_STORAGE_VERSION,
    },
  )
  const [friendRequests, setFriendRequests] = useStoredState(
    FRIEND_REQUEST_STORE_KEY,
    FriendRequestStoreSchema,
    {
      defaultValue: EMPTY_FRIEND_REQUEST_STORE,
      version: FRIEND_REQUEST_STORE_VERSION,
    },
  )
  const [friendAliases, setFriendAliases] = useStoredState(
    FRIEND_ALIAS_STORE_KEY,
    FriendAliasStoreSchema,
    {
      defaultValue: EMPTY_FRIEND_ALIAS_STORE,
      version: FRIEND_ALIAS_STORE_VERSION,
    },
  )
  const [challenges, setChallenges] = useStoredState(
    CHALLENGE_STORE_KEY,
    ChallengeStoreSchema,
    {
      defaultValue: EMPTY_CHALLENGE_STORE,
      version: CHALLENGE_STORE_VERSION,
    },
  )
  const [reports, setReports] = useStoredState(
    PLAYER_REPORT_STORAGE_KEY,
    PlayerReportStoreSchema,
    {
      defaultValue: EMPTY_PLAYER_REPORT_STORE,
      version: PLAYER_REPORT_STORAGE_VERSION,
    },
  )
  const [decks, , setDecks] = useUserIdbState(
    DECK_LIBRARY_STORAGE_KEY,
    DeckLibraryStateSchema,
    {
      defaultValue: EMPTY_DECK_LIBRARY,
      version: DECK_LIBRARY_STORAGE_VERSION,
    },
  )
  const [cards] = useUserIdbState(
    CARD_LIBRARY_STORAGE_KEY,
    CardLibraryStateSchema,
    {
      defaultValue: EMPTY_CARD_LIBRARY,
      version: CARD_LIBRARY_STORAGE_VERSION,
    },
  )
  const [heroes] = useUserIdbState(
    HERO_LIBRARY_STORAGE_KEY,
    HeroLibraryStateSchema,
    { defaultValue: EMPTY_HERO_LIBRARY, version: HERO_LIBRARY_STORAGE_VERSION },
  )
  const [systemBattleBots, setSystemBattleBots] = useStoredState(
    SYSTEM_BATTLE_BOTS_STORAGE_KEY,
    SystemBattleBotsStateSchema,
    {
      defaultValue: EMPTY_SYSTEM_BATTLE_BOTS,
      version: SYSTEM_BATTLE_BOTS_STORAGE_VERSION,
    },
  )
  const publicSystemBattleBotsQuery = usePublicSystemBattleBotsQuery()
  const resolvedSystemBattleBots =
    publicSystemBattleBotsQuery.data ?? systemBattleBots

  useEffect(() => {
    if (publicSystemBattleBotsQuery.data) {
      setSystemBattleBots(publicSystemBattleBotsQuery.data)
    }
  }, [publicSystemBattleBotsQuery.data, setSystemBattleBots])

  // Hot seat form
  const [hotSeatDeckA, setHotSeatDeckA] = useState("")
  const [hotSeatDeckB, setHotSeatDeckB] = useState("")
  const [hotSeatNameA, setHotSeatNameA] = useState(() =>
    t("match.page.player1"),
  )
  const [hotSeatNameB, setHotSeatNameB] = useState(() =>
    t("match.page.player2"),
  )
  const [hotSeatErrors, setHotSeatErrors] = useState<string[]>([])

  const [friendCodeInput, setFriendCodeInput] = useState("")
  const [friendError, setFriendError] = useState<string | null>(null)
  const [syncingFriendRequestIds, setSyncingFriendRequestIds] = useState<
    string[]
  >([])
  const [syncingChallengeIds, setSyncingChallengeIds] = useState<string[]>([])
  const notifiedFriendRequestIdsRef = useRef<Set<string>>(new Set())
  const notifiedChallengeIdsRef = useRef<Set<string>>(new Set())
  const challengeStatusByIdRef = useRef<Map<string, ChallengeRecord["status"]>>(
    new Map(),
  )
  const baseDocumentTitleRef = useRef<string | null>(null)
  const hasSeenInitialIncomingFriendRequestsRef = useRef(false)
  const hasSeenInitialPendingChallengesRef = useRef(false)
  const hasSeenInitialChallengeStatusesRef = useRef(false)
  const lookupPublicProfileByFriendCode = useLookupPublicProfileByFriendCode()
  const publishPublicProfileMutation = usePublishPublicProfileMutation()
  const syncFriendRequestMutation = useSyncFriendRequestMutation()
  const syncChallengeMutation = useSyncChallengeMutation()

  const publicProfileByTag = useMemo(
    () => new Map(publicProfiles.map((profile) => [profile.gamerTag, profile])),
    [publicProfiles],
  )
  const publicProfileByUserId = useMemo(
    () =>
      new Map(publicProfiles.map((profile) => [profile.ownerUserId, profile])),
    [publicProfiles],
  )
  const allFriendRequests = useMemo(
    () =>
      friendRequestStoreToList(friendRequests).sort((left, right) =>
        right.updatedAt.localeCompare(left.updatedAt),
      ),
    [friendRequests],
  )
  const acceptedFriends = useMemo<SocialProfile[]>(() => {
    if (!userId) return []

    return allFriendRequests
      .filter(
        (request) =>
          request.status === "accepted" &&
          (request.inviterUserId === userId ||
            request.requesterUserId === userId),
      )
      .map((request) => {
        const counterpartUserId =
          request.inviterUserId === userId
            ? request.requesterUserId
            : request.inviterUserId
        const fallbackTag =
          request.inviterUserId === userId
            ? request.requesterTag
            : request.inviterTag
        const counterpartProfile = publicProfileByUserId.get(counterpartUserId)
        const alias = friendAliases[counterpartUserId]

        return {
          friendCode: counterpartProfile?.friendCode,
          alias,
          avatarUrl: counterpartProfile?.avatarUrl,
          gamerTag: counterpartProfile?.gamerTag ?? fallbackTag,
          ownerUserId: counterpartUserId,
          updatedAt: counterpartProfile?.updatedAt,
        }
      })
      .sort((left, right) => left.gamerTag.localeCompare(right.gamerTag))
  }, [allFriendRequests, friendAliases, publicProfileByUserId, userId])
  const incomingFriendRequests = useMemo(
    () =>
      !userId
        ? []
        : allFriendRequests.filter(
            (request) =>
              request.inviterUserId === userId && request.status === "pending",
          ),
    [allFriendRequests, userId],
  )
  const outgoingFriendRequests = useMemo(
    () =>
      !userId
        ? []
        : allFriendRequests.filter(
            (request) =>
              request.requesterUserId === userId &&
              request.status === "pending",
          ),
    [allFriendRequests, userId],
  )
  const incomingFriendRequestsForNotifications = useMemo(
    () =>
      !userId
        ? []
        : allFriendRequests.filter(
            (request) =>
              request.inviterUserId === userId && request.status === "pending",
          ),
    [allFriendRequests, userId],
  )
  const allChallenges = useMemo(
    () =>
      challengeStoreToList(challenges).sort((left, right) =>
        right.updatedAt.localeCompare(left.updatedAt),
      ),
    [challenges],
  )
  const pendingCount = allChallenges.filter(
    (c) => c.challengedTag === myTag && c.status === "pending",
  ).length
  const socialSnapshotQuery = useSocialSnapshotQuery(
    pendingCount,
    Boolean(userId),
  )
  const remoteDecksQuery = useSessionQuery({
    enabled: Boolean(userId),
    queryFn: ({ sessionToken, signal }) =>
      fetchManagedDeckRecordsFromServer(sessionToken, signal),
    queryKey: queryKeys.library.decks(userId),
    refetchOnWindowFocus: true,
  })
  useEffect(() => {
    if (!remoteDecksQuery.data) return
    setDecks((previous) =>
      mergeAuthoritativeRemoteRecords(
        previous,
        remoteDecksQuery.data,
        (record) => record.deck.deckId,
      ),
    )
  }, [remoteDecksQuery.data, setDecks])
  const remoteRefreshFailed = socialSnapshotQuery.isError
  const remoteRefreshedAt =
    socialSnapshotQuery.dataUpdatedAt > 0
      ? new Date(socialSnapshotQuery.dataUpdatedAt).toISOString()
      : null
  const incomingPendingChallenges = useMemo(
    () =>
      allChallenges
        .filter(
          (challenge) =>
            challenge.challengedTag === myTag && challenge.status === "pending",
        )
        .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt)),
    [allChallenges, myTag],
  )

  const getSessionToken = useCallback(async (): Promise<string | null> => {
    if (!userId) return null
    return getToken()
  }, [getToken, userId])

  const publishMyProfile = useCallback(
    async (profile: PlayerProfile) => {
      if (!userId) return
      await publishPublicProfileMutation.mutateAsync(
        buildPublicPlayerProfile(profile, userId, user?.imageUrl),
      )
    },
    [publishPublicProfileMutation, user?.imageUrl, userId],
  )

  useEffect(() => {
    if (myProfile.friendCode) return

    const nextProfile: PlayerProfile = {
      ...myProfile,
      friendCode: createFriendCode(),
      updatedAt: new Date().toISOString(),
    }

    setProfiles((previous) => ({
      ...previous,
      [normalizeEmail(nextProfile.email)]: nextProfile,
    }))

    void publishMyProfile(nextProfile).catch(() => {
      // Keep the local profile usable even if remote publish is unavailable.
    })
  }, [myProfile, publishMyProfile, setProfiles])

  useEffect(() => {
    if (!socialSnapshotQuery.data) return

    setPublicProfiles(socialSnapshotQuery.data.publicProfiles)
    setFriendRequests((previous) =>
      mergeRemoteFriendRequestStore(
        previous,
        socialSnapshotQuery.data.friendRequests,
      ),
    )
    setChallenges((previous) =>
      mergeRemoteChallengeStore(previous, socialSnapshotQuery.data.challenges),
    )
  }, [
    setChallenges,
    setFriendRequests,
    setPublicProfiles,
    socialSnapshotQuery.data,
  ])

  useEffect(() => {
    if (typeof document === "undefined") return

    if (baseDocumentTitleRef.current == null) {
      baseDocumentTitleRef.current = document.title
    }

    const baseTitle = baseDocumentTitleRef.current ?? "CraftForce"
    document.title =
      pendingCount > 0 ? `(${pendingCount}) ${baseTitle}` : baseTitle

    return () => {
      document.title = baseTitle
    }
  }, [pendingCount])

  useEffect(() => {
    const requestIds = new Set(
      incomingFriendRequestsForNotifications.map(
        (request) => request.requestId,
      ),
    )

    if (!hasSeenInitialIncomingFriendRequestsRef.current) {
      hasSeenInitialIncomingFriendRequestsRef.current = true
      notifiedFriendRequestIdsRef.current = requestIds
      return
    }

    const newRequests = incomingFriendRequestsForNotifications.filter(
      (request) => !notifiedFriendRequestIdsRef.current.has(request.requestId),
    )
    notifiedFriendRequestIdsRef.current = requestIds

    if (newRequests.length === 0) return
    if (
      typeof document !== "undefined" &&
      document.visibilityState !== "hidden"
    )
      return
    if (typeof Notification === "undefined") return
    if (Notification.permission !== "granted") return

    for (const request of newRequests) {
      const notification = new Notification(
        t("friends.friendRequestNotification.title"),
        {
          body: t("friends.friendRequestNotification.body", {
            requesterTag: request.requesterTag,
          }),
          tag: `friend-request-${request.requestId}`,
        },
      )
      window.setTimeout(() => notification.close(), 8_000)
    }
  }, [incomingFriendRequestsForNotifications, t])

  useEffect(() => {
    const pendingIds = new Set(
      incomingPendingChallenges.map((challenge) => challenge.challengeId),
    )

    if (!hasSeenInitialPendingChallengesRef.current) {
      hasSeenInitialPendingChallengesRef.current = true
      notifiedChallengeIdsRef.current = pendingIds
      return
    }

    const newChallenges = incomingPendingChallenges.filter(
      (challenge) =>
        !notifiedChallengeIdsRef.current.has(challenge.challengeId),
    )
    notifiedChallengeIdsRef.current = pendingIds

    if (newChallenges.length === 0) return
    if (
      typeof document !== "undefined" &&
      document.visibilityState !== "hidden"
    )
      return
    if (typeof Notification === "undefined") return
    if (Notification.permission !== "granted") return

    for (const challenge of newChallenges) {
      const notification = new Notification(t("friends.notification.title"), {
        body: t("friends.notification.body", {
          challengerTag: challenge.challengerTag,
        }),
        tag: `challenge-${challenge.challengeId}`,
      })
      window.setTimeout(() => notification.close(), 8_000)
    }
  }, [incomingPendingChallenges, t])

  useEffect(() => {
    const nextStatuses = new Map(
      allChallenges.map((challenge) => [
        challenge.challengeId,
        challenge.status,
      ]),
    )

    if (!hasSeenInitialChallengeStatusesRef.current) {
      hasSeenInitialChallengeStatusesRef.current = true
      challengeStatusByIdRef.current = nextStatuses
      return
    }

    const changedChallenges = allChallenges.filter((challenge) => {
      const previousStatus = challengeStatusByIdRef.current.get(
        challenge.challengeId,
      )
      return previousStatus && previousStatus !== challenge.status
    })

    challengeStatusByIdRef.current = nextStatuses

    if (changedChallenges.length === 0) return
    if (
      typeof document !== "undefined" &&
      document.visibilityState !== "hidden"
    )
      return
    if (typeof Notification === "undefined") return
    if (Notification.permission !== "granted") return

    for (const challenge of changedChallenges) {
      if (!challenge.remoteSyncedAt) continue

      const counterpartTag =
        challenge.challengerTag === myTag
          ? challenge.challengedTag
          : challenge.challengerTag
      const body =
        challenge.status === "accepted"
          ? t("friends.notification.accepted", {
              playerTag: counterpartTag,
            })
          : challenge.status === "declined"
            ? t("friends.notification.declined", {
                playerTag: counterpartTag,
              })
            : challenge.status === "cancelled"
              ? t("friends.notification.cancelled", {
                  playerTag: counterpartTag,
                })
              : null

      if (!body) continue

      const notification = new Notification(
        t("friends.notification.statusTitle"),
        {
          body,
          tag: `challenge-status-${challenge.challengeId}`,
        },
      )
      window.setTimeout(() => notification.close(), 8_000)
    }
  }, [allChallenges, myTag, t])

  const syncFriendRequestRecord = useCallback(
    (record: FriendRequestRecord) => {
      if (!userId) return
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return
      }
      if (syncingFriendRequestIds.includes(record.requestId)) {
        return
      }

      setSyncingFriendRequestIds((previous) => [...previous, record.requestId])
      void syncFriendRequestMutation
        .mutateAsync(record)
        .then((syncedRecord) => {
          if (!syncedRecord) return
          setFriendRequests((previous) => ({
            ...previous,
            [syncedRecord.requestId]:
              markRecordSyncSucceeded<FriendRequestRecord>(syncedRecord),
          }))
        })
        .catch((error) => {
          setFriendRequests((previous) => {
            const existing = previous[record.requestId]
            if (!existing) return previous

            const nextRecord: FriendRequestRecord = isRateLimitError(error)
              ? existing
              : markRecordSyncFailed<FriendRequestRecord>(existing)

            return {
              ...previous,
              [record.requestId]: nextRecord,
            }
          })
        })
        .finally(() => {
          setSyncingFriendRequestIds((previous) =>
            previous.filter((requestId) => requestId !== record.requestId),
          )
        })
    },
    [
      setFriendRequests,
      syncFriendRequestMutation,
      syncingFriendRequestIds,
      userId,
    ],
  )

  useEffect(() => {
    if (!userId) return

    const retryableRecords = allFriendRequests.filter(
      (record) =>
        shouldAutoRetrySync(record) &&
        !syncingFriendRequestIds.includes(record.requestId),
    )

    if (retryableRecords.length === 0) return

    retryableRecords.forEach((record) => {
      syncFriendRequestRecord(record)
    })
  }, [
    allFriendRequests,
    syncFriendRequestRecord,
    syncingFriendRequestIds,
    userId,
  ])

  const syncChallengeRecord = useCallback(
    (record: ChallengeRecord) => {
      if (!userId) return
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return
      }
      if (
        syncingChallengeIds.includes(record.challengeId) ||
        !record.challengerUserId ||
        !record.challengedUserId
      ) {
        return
      }

      setSyncingChallengeIds((previous) => [...previous, record.challengeId])
      void syncChallengeMutation
        .mutateAsync(record)
        .then((syncedRecord) => {
          if (!syncedRecord) return
          setChallenges((previous) => ({
            ...previous,
            [syncedRecord.challengeId]:
              markRecordSyncSucceeded<ChallengeRecord>(syncedRecord),
          }))
        })
        .catch((error) => {
          setChallenges((previous) => {
            const existing = previous[record.challengeId]
            if (!existing) return previous

            const nextRecord: ChallengeRecord = isRateLimitError(error)
              ? existing
              : markRecordSyncFailed<ChallengeRecord>(existing)

            return {
              ...previous,
              [record.challengeId]: nextRecord,
            }
          })
        })
        .finally(() => {
          setSyncingChallengeIds((previous) =>
            previous.filter(
              (challengeId) => challengeId !== record.challengeId,
            ),
          )
        })
    },
    [setChallenges, syncChallengeMutation, syncingChallengeIds, userId],
  )

  useEffect(() => {
    if (!userId) return

    const retryableRecords = allChallenges.filter(
      (record) =>
        shouldAutoRetrySync(record) &&
        !syncingChallengeIds.includes(record.challengeId) &&
        Boolean(record.challengerUserId) &&
        Boolean(record.challengedUserId),
    )

    if (retryableRecords.length === 0) return

    retryableRecords.forEach((record) => {
      syncChallengeRecord(record)
    })
  }, [allChallenges, syncingChallengeIds, syncChallengeRecord, userId])

  function handleSendFriendRequest() {
    const normalizedCode = friendCodeInput.trim().toUpperCase()
    if (!normalizedCode) {
      setFriendError(t("friends.friendCode.required"))
      return
    }
    if (!userId) {
      setFriendError(t("friends.actionError.networkFailed"))
      return
    }
    void lookupPublicProfileByFriendCode(normalizedCode, getSessionToken)
      .then((targetProfile) => {
        if (!targetProfile?.ownerUserId) {
          setFriendError(t("friends.friendCode.notFound"))
          return
        }
        if (targetProfile.ownerUserId === userId) {
          setFriendError(t("friends.friendCode.self"))
          return
        }

        const existingRelationship = allFriendRequests.find((request) => {
          const samePair =
            (request.inviterUserId === targetProfile.ownerUserId &&
              request.requesterUserId === userId) ||
            (request.inviterUserId === userId &&
              request.requesterUserId === targetProfile.ownerUserId)
          return (
            samePair &&
            (request.status === "pending" || request.status === "accepted")
          )
        })
        if (existingRelationship) {
          setFriendError(
            existingRelationship.status === "accepted"
              ? t("friends.friendCode.alreadyFriends")
              : t("friends.friendCode.pendingExists"),
          )
          return
        }

        const now = new Date().toISOString()
        const nextRecord: FriendRequestRecord =
          markRecordSyncPending<FriendRequestRecord>({
            requestId: createFriendRequestId(),
            inviterTag: targetProfile.gamerTag,
            inviterUserId: targetProfile.ownerUserId,
            requesterTag: myTag,
            requesterUserId: userId,
            createdAt: now,
            status: "pending",
            updatedAt: now,
          })

        setFriendError(null)
        setFriendCodeInput("")
        setFriendRequests((previous) => ({
          ...previous,
          [nextRecord.requestId]: nextRecord,
        }))
        syncFriendRequestRecord(nextRecord)
      })
      .catch(() => {
        setFriendError(t("friends.actionError.networkFailed"))
      })
  }

  function handleRegenerateFriendCode() {
    if (!userId) {
      setFriendError(t("friends.actionError.networkFailed"))
      return
    }

    const nextProfile: PlayerProfile = {
      ...myProfile,
      friendCode: createFriendCode(),
      updatedAt: new Date().toISOString(),
    }

    setProfiles((previous) => ({
      ...previous,
      [normalizeEmail(nextProfile.email)]: nextProfile,
    }))

    void publishMyProfile(nextProfile)
      .then(() => {
        setFriendError(null)
      })
      .catch(() => {
        setFriendError(t("friends.actionError.networkFailed"))
      })
  }

  function handleAcceptFriendRequest(requestId: string) {
    const existing = friendRequests[requestId]
    if (!existing) return
    const now = new Date().toISOString()
    const acceptedRecord = markRecordSyncPending<FriendRequestRecord>({
      ...existing,
      status: "accepted",
      updatedAt: now,
    })
    setFriendRequests((previous) => ({
      ...previous,
      [requestId]: acceptedRecord,
    }))
    syncFriendRequestRecord(acceptedRecord)
  }

  function handleDeclineFriendRequest(requestId: string) {
    const existing = friendRequests[requestId]
    if (!existing) return
    const now = new Date().toISOString()
    const declinedRecord = markRecordSyncPending<FriendRequestRecord>({
      ...existing,
      status: "declined",
      updatedAt: now,
    })
    setFriendRequests((previous) => ({
      ...previous,
      [requestId]: declinedRecord,
    }))
    syncFriendRequestRecord(declinedRecord)
  }

  function handleCancelFriendRequest(requestId: string) {
    const existing = friendRequests[requestId]
    if (!existing) return
    const now = new Date().toISOString()
    const cancelledRecord = markRecordSyncPending<FriendRequestRecord>({
      ...existing,
      status: "cancelled",
      updatedAt: now,
    })
    setFriendRequests((previous) => ({
      ...previous,
      [requestId]: cancelledRecord,
    }))
    syncFriendRequestRecord(cancelledRecord)
  }

  function handleSendChallenge(challengedTag: string, deckId: string) {
    const now = new Date().toISOString()
    const challengeId = createChallengeId()
    const challengedProfile = publicProfileByTag.get(challengedTag)

    if (!challengedProfile?.ownerUserId) {
      setActionError(t("friends.actionError.playerSyncUnavailable"))
      return
    }

    if (!userId) {
      setActionError(t("friends.actionError.networkFailed"))
      return
    }

    const seatResult = buildSeatInput(
      deckId,
      "seat_a",
      myTag,
      myTag,
      decks,
      cards,
      heroes,
    )
    if (seatResult.error) {
      setActionError(getMatchSetupErrorLabel(t, seatResult.error))
      return
    }

    const nextRecord: ChallengeRecord = markRecordSyncPending<ChallengeRecord>({
      challengeId,
      challengerDeckId: deckId,
      challengerSeatInput: {
        ...seatResult.input,
        mercCards: [...seatResult.input.mercCards],
      },
      challengedTag,
      challengedUserId: challengedProfile.ownerUserId,
      challengerTag: myTag,
      challengerUserId: userId,
      createdAt: now,
      status: "pending",
      updatedAt: now,
    })

    setActionError(null)
    setChallenges((prev) => ({
      ...prev,
      [challengeId]: nextRecord,
    }))
    syncChallengeRecord(nextRecord)
  }

  async function handleAcceptChallenge(challengeId: string, myDeckId: string) {
    const challenge = challenges[challengeId]
    if (!challenge) {
      setActionError(t("friends.actionError.challengeUnavailable"))
      return
    }

    const now = new Date().toISOString()
    const challengerTag = challenge.challengerTag

    const seatAResult = challenge.challengerSeatInput
      ? { input: challenge.challengerSeatInput, error: null }
      : buildSeatInput(
          challenge.challengerDeckId,
          "seat_a",
          challengerTag,
          challengerTag,
          decks,
          cards,
          heroes,
        )
    const seatBResult = buildSeatInput(
      myDeckId,
      "seat_b",
      myTag,
      myTag,
      decks,
      cards,
      heroes,
    )

    if (seatAResult.error || seatBResult.error) {
      setActionError(
        seatAResult.error
          ? getMatchSetupErrorLabel(t, seatAResult.error)
          : seatBResult.error
            ? getMatchSetupErrorLabel(t, seatBResult.error)
            : t("friends.actionError.selectedDecksInvalid"),
      )
      return
    }

    const matchId = `match_${Date.now().toString(36)}`
    const seed = `challenge_${challengeId}_${Date.now()}`
    const apiBase = getApiBaseUrl()
    if (!apiBase) {
      setActionError(t("friends.actionError.networkFailed"))
      return
    }

    try {
      const res = await fetch(`${apiBase}/api/match/${matchId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          matchId,
          seatA: seatAResult.input,
          seatB: seatBResult.input,
          seed,
        }),
      })
      throwIfRateLimited(res)
      if (!res.ok) {
        setActionError(t("friends.actionError.initializeFailed"))
        return
      }
    } catch (err) {
      if (!isRateLimitError(err)) {
        console.error("Network error initializing match:", err)
      }
      setActionError(t("friends.actionError.networkFailed"))
      return
    }

    setActionError(null)
    storePendingOnlineMatch({ matchId, seatId: "seat_b", challengeId })
    const acceptedRecord: ChallengeRecord =
      markRecordSyncPending<ChallengeRecord>({
        ...challenge,
        challengedDeckId: myDeckId,
        matchId,
        status: "accepted",
        updatedAt: now,
      })

    setChallenges((prev) => ({
      ...prev,
      [challengeId]: acceptedRecord,
    }))
    syncChallengeRecord(acceptedRecord)

    router.navigate("/play/match")
  }

  function handleJoinChallenge(matchId: string, seatId: "seat_a" | "seat_b") {
    storePendingOnlineMatch({ matchId, seatId, challengeId: "" })
    router.navigate("/play/match")
  }

  function handleStartHotSeat() {
    const errs: string[] = []
    if (!hotSeatDeckA) errs.push(t("match.setup.error.player1Deck"))
    if (!hotSeatDeckB) errs.push(t("match.setup.error.player2Deck"))
    if (errs.length > 0) {
      setHotSeatErrors(errs)
      return
    }

    const nameA = hotSeatNameA.trim() || t("match.page.player1")
    const nameB = hotSeatNameB.trim() || t("match.page.player2")

    const seatAResult = buildSeatInput(
      hotSeatDeckA,
      "seat_a",
      "player_1",
      nameA,
      decks,
      cards,
      heroes,
    )
    const seatBResult = buildSeatInput(
      hotSeatDeckB,
      "seat_b",
      "player_2",
      nameB,
      decks,
      cards,
      heroes,
    )

    if (seatAResult.error)
      errs.push(getMatchSetupErrorLabel(t, seatAResult.error))
    if (seatBResult.error)
      errs.push(getMatchSetupErrorLabel(t, seatBResult.error))
    if (errs.length > 0) {
      setHotSeatErrors(errs)
      return
    }

    if (!seatAResult.input || !seatBResult.input) {
      setHotSeatErrors([t("match.setup.error.deckLoad")])
      return
    }

    const { state } = createMatch({
      matchId: `match_${Date.now().toString(36)}`,
      seatA: seatAResult.input,
      seatB: seatBResult.input,
      seed: `hotseat_${Date.now()}`,
      systemBattleBots: resolvedSystemBattleBots,
    })

    setHotSeatErrors([])
    storePendingMatch({ matchState: state, challengeId: "", hotSeat: true })
    router.navigate("/play/match")
  }

  function handleDeclineChallenge(challengeId: string) {
    const existing = challenges[challengeId]
    if (!existing) {
      setActionError(t("friends.actionError.challengeUnavailable"))
      return
    }
    const now = new Date().toISOString()
    setActionError(null)
    const declinedRecord: ChallengeRecord =
      markRecordSyncPending<ChallengeRecord>({
        ...existing,
        status: "declined",
        updatedAt: now,
      })
    setChallenges((prev) => ({
      ...prev,
      [challengeId]: declinedRecord,
    }))
    syncChallengeRecord(declinedRecord)
  }

  function handleCancelChallenge(challengeId: string) {
    const existing = challenges[challengeId]
    if (!existing) {
      setActionError(t("friends.actionError.challengeUnavailable"))
      return
    }
    const now = new Date().toISOString()
    setActionError(null)
    const cancelledRecord: ChallengeRecord =
      markRecordSyncPending<ChallengeRecord>({
        ...existing,
        status: "cancelled",
        updatedAt: now,
      })
    setChallenges((prev) => ({
      ...prev,
      [challengeId]: cancelledRecord,
    }))
    syncChallengeRecord(cancelledRecord)
  }

  function handleReportPlayer(reportedTag: string, reason: string) {
    setReports((previous) => [
      createPlayerReport(myTag, reportedTag, reason),
      ...previous,
    ])
  }

  function handleSetFriendAlias(ownerUserId: string, alias: string | null) {
    setFriendAliases((previous) => {
      if (!alias) {
        const nextAliases = { ...previous }
        delete nextAliases[ownerUserId]
        return nextAliases
      }
      return {
        ...previous,
        [ownerUserId]: alias,
      }
    })
  }

  return (
    <>
      {/* Hot seat setup */}
      <section className="hot-seat-setup">
        <h3 className="hot-seat-setup-heading">{t("match.hotSeat.heading")}</h3>
        <div className="match-setup-form">
          <div className="match-setup-seat">
            <h4>{t("match.page.player1Seat")}</h4>
            <div className="field">
              <label htmlFor="hs-name-a">
                <span>{t("common.displayName")}</span>
              </label>
              <input
                className="text-input"
                id="hs-name-a"
                onChange={(e) => setHotSeatNameA(e.target.value)}
                type="text"
                value={hotSeatNameA}
              />
            </div>
            <div className="field">
              <label htmlFor="hs-deck-a">
                <span>{t("common.deck")}</span>
              </label>
              <select
                className="text-input"
                id="hs-deck-a"
                onChange={(e) => setHotSeatDeckA(e.target.value)}
                value={hotSeatDeckA}
              >
                <option value="">{t("friends.deckPicker.placeholder")}</option>
                {decks.map((r) => (
                  <option key={r.deck.deckId} value={r.deck.deckId}>
                    {r.deck.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="match-setup-vs">{t("match.page.vs")}</div>

          <div className="match-setup-seat">
            <h4>{t("match.page.player2Seat")}</h4>
            <div className="field">
              <label htmlFor="hs-name-b">
                <span>{t("common.displayName")}</span>
              </label>
              <input
                className="text-input"
                id="hs-name-b"
                onChange={(e) => setHotSeatNameB(e.target.value)}
                type="text"
                value={hotSeatNameB}
              />
            </div>
            <div className="field">
              <label htmlFor="hs-deck-b">
                <span>{t("common.deck")}</span>
              </label>
              <select
                className="text-input"
                id="hs-deck-b"
                onChange={(e) => setHotSeatDeckB(e.target.value)}
                value={hotSeatDeckB}
              >
                <option value="">{t("friends.deckPicker.placeholder")}</option>
                {decks.map((r) => (
                  <option key={r.deck.deckId} value={r.deck.deckId}>
                    {r.deck.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {hotSeatErrors.length > 0 && (
          <ul className="form-errors">
            {hotSeatErrors.map((e) => (
              <li className="field-error" key={e}>
                {e}
              </li>
            ))}
          </ul>
        )}

        <div className="match-setup-actions">
          <button
            className="primary-button"
            onClick={handleStartHotSeat}
            type="button"
          >
            {t("match.hotSeat.startButton")}
          </button>
        </div>
      </section>

      <div className="section-tabs">
        <button
          className={`section-tab${tab === "players" ? " section-tab-active" : ""}`}
          onClick={() => {
            setTab("players")
            setActionError(null)
          }}
          type="button"
        >
          {t("friends.friendsTab")}
        </button>
        <button
          className={`section-tab${tab === "challenges" ? " section-tab-active" : ""}`}
          onClick={() => {
            setTab("challenges")
            setActionError(null)
          }}
          type="button"
        >
          {t("common.challenges")}
          {pendingCount > 0 && (
            <span className="tab-badge">{pendingCount}</span>
          )}
        </button>
      </div>

      <LibraryRefreshStatus
        failed={remoteRefreshFailed}
        lastRefreshedAt={remoteRefreshedAt}
      />

      {tab === "players" && pendingCount > 0 && (
        <div className="friends-challenge-callout">
          <div className="friends-challenge-callout-copy">
            <strong>{t("friends.inboxCallout.title")}</strong>
            <span>
              {pendingCount === 1 && incomingPendingChallenges[0]
                ? t("friends.inboxCallout.single", {
                    challengerTag: incomingPendingChallenges[0].challengerTag,
                  })
                : t("friends.inboxCallout.multiple", {
                    count: pendingCount,
                  })}
            </span>
          </div>
          <button
            className="primary-button"
            onClick={() => setTab("challenges")}
            type="button"
          >
            {t("friends.openChallenges")}
          </button>
        </div>
      )}

      {tab === "players" && (
        <>
          {myProfile.friendCode && (
            <FriendCodePanel
              accountName={
                user?.fullName?.trim() ||
                [user?.firstName?.trim(), user?.lastName?.trim()]
                  .filter(Boolean)
                  .join(" ") ||
                null
              }
              avatarUrl={user?.imageUrl ?? null}
              friendCode={myProfile.friendCode}
              gamerTag={myTag}
              onRegenerate={handleRegenerateFriendCode}
            />
          )}

          <section className="friends-add-panel">
            <label className="field">
              <span>{t("friends.friendCode.enterLabel")}</span>
              <input
                className="text-input"
                onChange={(event) => setFriendCodeInput(event.target.value)}
                placeholder={t("friends.friendCode.placeholder")}
                value={friendCodeInput}
              />
            </label>
            {friendError && (
              <strong className="field-error">{friendError}</strong>
            )}
            <button
              className="primary-button"
              onClick={handleSendFriendRequest}
              type="button"
            >
              {t("friends.friendCode.send")}
            </button>
          </section>

          <FriendRequestSections
            incoming={incomingFriendRequests}
            onAccept={handleAcceptFriendRequest}
            onCancel={handleCancelFriendRequest}
            onDecline={handleDeclineFriendRequest}
            outgoing={outgoingFriendRequests}
          />

          <PlayersList
            challenges={allChallenges}
            myTag={myTag}
            onChallenge={handleSendChallenge}
            onReport={handleReportPlayer}
            onSetAlias={handleSetFriendAlias}
            profiles={acceptedFriends}
            reports={reports}
          />
        </>
      )}

      {tab === "challenges" && (
        <ChallengesPanel
          errorMessage={actionError}
          challenges={allChallenges}
          myTag={myTag}
          onAccept={handleAcceptChallenge}
          onCancel={handleCancelChallenge}
          onDecline={handleDeclineChallenge}
          onJoin={handleJoinChallenge}
          profiles={acceptedFriends}
        />
      )}
    </>
  )
}

export function FriendsPage() {
  const { t } = useI18n()
  const { isLoaded, userId } = useAuth()
  const { user } = useUser()
  const { access, isChecking } = useRemoteAccessDecision()
  const [profiles] = useStoredState(
    PLAYER_PROFILES_STORAGE_KEY,
    PlayerProfileStoreStateSchema,
    {
      defaultValue: EMPTY_PLAYER_PROFILE_STORE,
      version: PLAYER_PROFILES_STORAGE_VERSION,
    },
  )
  const email = user?.primaryEmailAddress?.emailAddress ?? null
  const myProfile = email
    ? getProfileByEmail(profiles, normalizeEmail(email))
    : null

  return (
    <section className="panel management-panel">
      <div className="page-header">
        <div>
          <p className="eyebrow">{t("friends.page.eyebrow")}</p>
          <h2>{t("friends.page.title")}</h2>
          <p className="lede table-lede">{t("friends.challengeAnother")}</p>
        </div>
      </div>

      {!isLoaded && <p className="meta">{t("friends.loading")}</p>}

      {isLoaded && !userId && <p>{t("friends.joinPrompt")}</p>}

      {isLoaded && userId && isChecking && (
        <p className="meta">{t("auth.loadingBody")}</p>
      )}

      {isLoaded &&
        userId &&
        !isChecking &&
        !myProfile &&
        (access === "invited" || access === "bootstrap_admin") && (
          <ProfileBootstrap access={access} email={email ?? ""} />
        )}

      {isLoaded &&
        userId &&
        !isChecking &&
        !myProfile &&
        access !== "invited" &&
        access !== "bootstrap_admin" && (
          <p className="meta">{t("friends.noProfile")}</p>
        )}

      {isLoaded && userId && myProfile && (
        <FriendsContent myProfile={myProfile} />
      )}
    </section>
  )
}
